import React, { useState } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import OrgDeletionModal from "../../components/OrgDeletionModal"
import {
  useGetAdminDataExplorerQuery,
  useLazyGetDataExplorerUserDetailsQuery,
  useLazyGetDataExplorerOrgDetailsQuery,
  useAssignSimulationToUserMutation,
  useUnlockMeasureForOrgMutation,
  useAssignSimulationToOrgMutation,
  useGetAvailableMeasuresQuery,
  useGetAvailableSimulationsQuery,
  useUpdateOrgMetadataMutation,
  useArchiveOrganizationMutation,
  DataExplorerUserDetailsData,
  DataExplorerOrgDetailsData,
  AvailableMeasure,
  AvailableSimulation,
} from "../../app/services/meseeknaApiV2"
import { FiSearch, FiUser, FiUsers, FiChevronDown, FiChevronUp, FiX, FiCheck, FiPlus, FiUnlock, FiTrash2, FiArchive, FiTag, FiEdit2, FiSave } from "react-icons/fi"
import { HiOutlineOfficeBuilding } from "react-icons/hi"
import { MdOutlineExplore, MdOutlineAssessment } from "react-icons/md"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SearchIcon = (props: { size?: number; className?: string }): JSX.Element => <FiSearch {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UserIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUser {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UsersIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUsers {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ExploreIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineExplore {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const AssessmentIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineAssessment {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronDownIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronDown {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronUpIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronUp {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const XIcon = (props: { size?: number; className?: string }): JSX.Element => <FiX {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckIcon = (props: { size?: number; className?: string }): JSX.Element => <FiCheck {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const PlusIcon = (props: { size?: number; className?: string }): JSX.Element => <FiPlus {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UnlockIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUnlock {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TrashIcon = (props: { size?: number; className?: string }): JSX.Element => <FiTrash2 {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ArchiveIcon = (props: { size?: number; className?: string }): JSX.Element => <FiArchive {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TagIcon = (props: { size?: number; className?: string }): JSX.Element => <FiTag {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const EditIcon = (props: { size?: number; className?: string }): JSX.Element => <FiEdit2 {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SaveIcon = (props: { size?: number; className?: string }): JSX.Element => <FiSave {...props} />

type SearchType = "all" | "users" | "simulations" | "organizations"

// User Details Panel Component
const UserDetailsPanel: React.FC<{
  data: DataExplorerUserDetailsData
  onClose: () => void
  onAssignSimulation: (userId: number, testId: number) => Promise<void>
  isAssigning: boolean
  availableSimulations: AvailableSimulation[]
}> = ({ data, onClose, onAssignSimulation, isAssigning, availableSimulations }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    simulations: true,
    scores: false,
    microlearning: false,
  })
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [selectedTestId, setSelectedTestId] = useState<number | null>(null)

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  const formatDate = (date: string | null) => {
    if (!date) return "N/A"
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: "bg-green-100 text-green-700",
      in_progress: "bg-blue-100 text-blue-700",
      not_started: "bg-gray-100 text-gray-700",
      approved: "bg-emerald-100 text-emerald-700",
    }
    return styles[status.toLowerCase()] || "bg-gray-100 text-gray-700"
  }

  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-2 rounded-r-lg">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="font-semibold text-lg text-gray-900">
            {data.user.firstName || data.user.lastName
              ? `${data.user.firstName || ""} ${data.user.lastName || ""}`.trim()
              : "No Name"}
          </h3>
          <p className="text-sm text-gray-600">{data.user.email}</p>
          {data.user.organizationName && (
            <p className="text-sm text-gray-500 mt-1">
              <BuildingIcon size={14} className="inline mr-1" />
              {data.user.organizationName}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            Role: {data.user.role || "User"} | Joined: {formatDate(data.user.createdAt)} | Last Login: {formatDate(data.user.lastLogin)}
          </p>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded">
          <XIcon size={18} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-3 rounded-lg shadow-sm">
          <p className="text-xs text-gray-500">Simulations</p>
          <p className="text-xl font-bold text-gray-900">
            {data.summary.completedSimulations}/{data.summary.totalSimulations}
          </p>
          <p className="text-xs text-gray-400">completed</p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm">
          <p className="text-xs text-gray-500">Avg Score</p>
          <p className="text-xl font-bold text-gray-900">
            {data.summary.averageScore !== null ? `${data.summary.averageScore.toFixed(1)}%` : "N/A"}
          </p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm">
          <p className="text-xs text-gray-500">Microlearning</p>
          <p className="text-xl font-bold text-gray-900">
            {data.summary.microlearningCompleted}/{data.summary.microlearningEnrolled}
          </p>
          <p className="text-xs text-gray-400">completed</p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm flex items-center justify-center">
          <button
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <PlusIcon size={16} />
            Assign Sim
          </button>
        </div>
      </div>

      {/* Simulations Section */}
      <div className="bg-white rounded-lg mb-3 overflow-hidden">
        <button
          onClick={() => toggleSection("simulations")}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">Assigned Simulations ({data.assignedSimulations.length})</span>
          {expandedSections.simulations ? <ChevronUpIcon size={18} /> : <ChevronDownIcon size={18} />}
        </button>
        {expandedSections.simulations && data.assignedSimulations.length > 0 && (
          <div className="border-t border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2 font-medium text-gray-600">Simulation</th>
                  <th className="text-left p-2 font-medium text-gray-600">Status</th>
                  <th className="text-left p-2 font-medium text-gray-600">Assigned</th>
                  <th className="text-left p-2 font-medium text-gray-600">Completed</th>
                  <th className="text-left p-2 font-medium text-gray-600">Top Score</th>
                </tr>
              </thead>
              <tbody>
                {data.assignedSimulations.map((sim) => {
                  const topScore = sim.scores.length > 0 ? Math.max(...sim.scores.map((s) => s.score)) : null
                  return (
                    <tr key={sim.id} className="border-t border-gray-50 hover:bg-gray-50">
                      <td className="p-2">
                        <p className="font-medium text-gray-900">{sim.testTitle || sim.testShortName || "Unknown"}</p>
                        {sim.testShortName && sim.testTitle && (
                          <p className="text-xs text-gray-500">{sim.testShortName}</p>
                        )}
                      </td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(sim.status)}`}>
                          {sim.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="p-2 text-gray-600">{formatDate(sim.dateAssigned)}</td>
                      <td className="p-2 text-gray-600">{formatDate(sim.dateUserEnd)}</td>
                      <td className="p-2">
                        {topScore !== null ? (
                          <span className="font-medium text-gray-900">{topScore.toFixed(1)}%</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {expandedSections.simulations && data.assignedSimulations.length === 0 && (
          <p className="p-3 text-gray-500 text-sm border-t border-gray-100">No simulations assigned</p>
        )}
      </div>

      {/* Scores Section */}
      <div className="bg-white rounded-lg mb-3 overflow-hidden">
        <button
          onClick={() => toggleSection("scores")}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">All Scores</span>
          {expandedSections.scores ? <ChevronUpIcon size={18} /> : <ChevronDownIcon size={18} />}
        </button>
        {expandedSections.scores && (
          <div className="border-t border-gray-100 p-3">
            {data.assignedSimulations.flatMap((sim) => sim.scores).length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {data.assignedSimulations.flatMap((sim) =>
                  sim.scores.map((score) => (
                    <div key={`${sim.id}-${score.measureId}`} className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-500 truncate">{score.measureName}</p>
                      <p className="font-semibold text-gray-900">{score.score.toFixed(1)}%</p>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No scores available</p>
            )}
          </div>
        )}
      </div>

      {/* Microlearning Section */}
      <div className="bg-white rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection("microlearning")}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">Microlearning ({data.microlearning.length})</span>
          {expandedSections.microlearning ? <ChevronUpIcon size={18} /> : <ChevronDownIcon size={18} />}
        </button>
        {expandedSections.microlearning && data.microlearning.length > 0 && (
          <div className="border-t border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2 font-medium text-gray-600">Measure</th>
                  <th className="text-left p-2 font-medium text-gray-600">Status</th>
                  <th className="text-left p-2 font-medium text-gray-600">Enrolled</th>
                  <th className="text-left p-2 font-medium text-gray-600">Completed</th>
                </tr>
              </thead>
              <tbody>
                {data.microlearning.map((ml) => (
                  <tr key={ml.id} className="border-t border-gray-50 hover:bg-gray-50">
                    <td className="p-2 font-medium text-gray-900">{ml.measureName}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(ml.status)}`}>
                        {ml.status}
                      </span>
                    </td>
                    <td className="p-2 text-gray-600">{formatDate(ml.createdAt)}</td>
                    <td className="p-2 text-gray-600">{formatDate(ml.completedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {expandedSections.microlearning && data.microlearning.length === 0 && (
          <p className="p-3 text-gray-500 text-sm border-t border-gray-100">No microlearning enrolled</p>
        )}
      </div>

      {/* Assign Simulation Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Assign Simulation</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a simulation to assign to {data.user.firstName || data.user.email}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Simulation</label>
              <select
                value={selectedTestId || ""}
                onChange={(e) => setSelectedTestId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a simulation...</option>
                {availableSimulations.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.title || s.shortName || `Test #${s.id}`}
                  </option>
                ))}
              </select>
              {availableSimulations.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">No simulations available.</p>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowAssignModal(false); setSelectedTestId(null) }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedTestId) {
                    await onAssignSimulation(data.user.id, selectedTestId)
                    setShowAssignModal(false)
                    setSelectedTestId(null)
                  }
                }}
                disabled={!selectedTestId || isAssigning}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAssigning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Assigning...
                  </>
                ) : (
                  <>
                    <CheckIcon size={16} />
                    Assign
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Organization Details Panel Component
const OrgDetailsPanel: React.FC<{
  data: DataExplorerOrgDetailsData
  onClose: () => void
  onUnlockMeasure: (organizationId: number, measureId: number) => Promise<void>
  onAssignSimulation: (organizationId: number, testId: number) => Promise<void>
  onUpdateMetadata: (organizationId: number, adminTags: string, adminNotes: string) => Promise<void>
  onArchive: (organizationId: number, archive: boolean) => Promise<void>
  onOpenDeleteModal: (organizationId: number, organizationName: string) => void
  availableMeasures: AvailableMeasure[]
  availableSimulations: AvailableSimulation[]
  isUnlocking: boolean
  isAssigning: boolean
  isUpdatingMetadata: boolean
  isArchiving: boolean
  initialTags?: string | null
  initialNotes?: string | null
  isArchived?: boolean
}> = ({ data, onClose, onUnlockMeasure, onAssignSimulation, onUpdateMetadata, onArchive, onOpenDeleteModal, availableMeasures, availableSimulations, isUnlocking, isAssigning, isUpdatingMetadata, isArchiving, initialTags, initialNotes, isArchived }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    users: true,
    simulations: false,
    measures: false,
    management: false,
  })
  const [showUnlockModal, setShowUnlockModal] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [selectedMeasureId, setSelectedMeasureId] = useState<number | null>(null)
  const [selectedTestId, setSelectedTestId] = useState<number | null>(null)
  const [editingTags, setEditingTags] = useState(false)
  const [editingNotes, setEditingNotes] = useState(false)
  const [tagsValue, setTagsValue] = useState(initialTags || "")
  const [notesValue, setNotesValue] = useState(initialNotes || "")
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  const formatDate = (date: string | null) => {
    if (!date) return "N/A"
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const handleSaveTags = async () => {
    await onUpdateMetadata(data.organization.id, tagsValue, notesValue)
    setEditingTags(false)
  }

  const handleSaveNotes = async () => {
    await onUpdateMetadata(data.organization.id, tagsValue, notesValue)
    setEditingNotes(false)
  }

  // Filter out already unlocked measures
  const unlockedMeasureIds = new Set(data.measures.filter(m => m.isActive).map(m => m.measureId))
  const measuresToUnlock = availableMeasures.filter(m => !unlockedMeasureIds.has(m.id))

  // Filter out already assigned simulations
  const assignedTestIds = new Set(data.availableSimulations.map(s => s.testId))
  const simsToAssign = availableSimulations.filter(s => !assignedTestIds.has(s.id))

  return (
    <div className={`${isArchived ? "bg-gray-100 border-l-4 border-gray-400" : "bg-green-50 border-l-4 border-green-500"} p-4 mt-2 rounded-r-lg`}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg text-gray-900">{data.organization.organizationName}</h3>
            {isArchived && (
              <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">Archived</span>
            )}
          </div>
          <p className="text-sm text-gray-600">
            Created: {formatDate(data.organization.createdAt)}
          </p>
          <div className="flex gap-2 mt-1 flex-wrap">
            {data.organization.onboardingCompleted ? (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Onboarding Complete</span>
            ) : (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">Onboarding Pending</span>
            )}
            {data.organization.subscriptionStatus && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                {data.organization.subscriptionStatus}
              </span>
            )}
            {tagsValue && tagsValue.split(",").map((tag, idx) => (
              <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                <TagIcon size={10} />
                {tag.trim()}
              </span>
            ))}
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded">
          <XIcon size={18} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-white p-3 rounded-lg shadow-sm">
          <p className="text-xs text-gray-500">Users</p>
          <p className="text-xl font-bold text-gray-900">{data.summary.totalUsers}</p>
          <p className="text-xs text-gray-400">{data.summary.activeUsers} active</p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm">
          <p className="text-xs text-gray-500">Simulations</p>
          <p className="text-xl font-bold text-gray-900">{data.summary.totalSimulationsAssigned}</p>
          <p className="text-xs text-gray-400">{data.summary.totalSimulationsCompleted} completed</p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm">
          <p className="text-xs text-gray-500">Completion Rate</p>
          <p className="text-xl font-bold text-gray-900">
            {data.summary.totalSimulationsAssigned > 0
              ? `${((data.summary.totalSimulationsCompleted / data.summary.totalSimulationsAssigned) * 100).toFixed(0)}%`
              : "N/A"}
          </p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm flex items-center justify-center">
          <button
            onClick={() => setShowUnlockModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
          >
            <UnlockIcon size={16} />
            Unlock Measure
          </button>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm flex items-center justify-center">
          <button
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
          >
            <PlusIcon size={16} />
            Assign Sim
          </button>
        </div>
      </div>

      {/* Unlock Measure Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Unlock Measure</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a measure to unlock for {data.organization.organizationName}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Measure</label>
              <select
                value={selectedMeasureId || ""}
                onChange={(e) => setSelectedMeasureId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Select a measure...</option>
                {measuresToUnlock.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.measureName} {m.measureCode ? `(${m.measureCode})` : ""}
                  </option>
                ))}
              </select>
              {measuresToUnlock.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">All measures are already unlocked for this organization.</p>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowUnlockModal(false); setSelectedMeasureId(null) }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedMeasureId) {
                    await onUnlockMeasure(data.organization.id, selectedMeasureId)
                    setShowUnlockModal(false)
                    setSelectedMeasureId(null)
                  }
                }}
                disabled={!selectedMeasureId || isUnlocking}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUnlocking ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Unlocking...
                  </>
                ) : (
                  <>
                    <UnlockIcon size={16} />
                    Unlock
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Simulation Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Assign Simulation</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a simulation to assign to {data.organization.organizationName}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Simulation</label>
              <select
                value={selectedTestId || ""}
                onChange={(e) => setSelectedTestId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Select a simulation...</option>
                {simsToAssign.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.title || s.shortName || `Test #${s.id}`}
                  </option>
                ))}
              </select>
              {simsToAssign.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">All simulations are already assigned to this organization.</p>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowAssignModal(false); setSelectedTestId(null) }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedTestId) {
                    await onAssignSimulation(data.organization.id, selectedTestId)
                    setShowAssignModal(false)
                    setSelectedTestId(null)
                  }
                }}
                disabled={!selectedTestId || isAssigning}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAssigning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Assigning...
                  </>
                ) : (
                  <>
                    <CheckIcon size={16} />
                    Assign
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Section */}
      <div className="bg-white rounded-lg mb-3 overflow-hidden">
        <button
          onClick={() => toggleSection("users")}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">Users ({data.users.length})</span>
          {expandedSections.users ? <ChevronUpIcon size={18} /> : <ChevronDownIcon size={18} />}
        </button>
        {expandedSections.users && data.users.length > 0 && (
          <div className="border-t border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2 font-medium text-gray-600">Name</th>
                  <th className="text-left p-2 font-medium text-gray-600">Email</th>
                  <th className="text-left p-2 font-medium text-gray-600">Role</th>
                  <th className="text-left p-2 font-medium text-gray-600">Completed</th>
                  <th className="text-left p-2 font-medium text-gray-600">Last Active</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((user) => (
                  <tr key={user.id} className="border-t border-gray-50 hover:bg-gray-50">
                    <td className="p-2 font-medium text-gray-900">
                      {user.firstName || user.lastName
                        ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                        : "No Name"}
                    </td>
                    <td className="p-2 text-gray-600">{user.email}</td>
                    <td className="p-2">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                        {user.role || "User"}
                      </span>
                    </td>
                    <td className="p-2 text-gray-600">{user.simulationsCompleted}</td>
                    <td className="p-2 text-gray-600">{formatDate(user.lastActive)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {expandedSections.users && data.users.length === 0 && (
          <p className="p-3 text-gray-500 text-sm border-t border-gray-100">No users in organization</p>
        )}
      </div>

      {/* Simulations Activity Section */}
      <div className="bg-white rounded-lg mb-3 overflow-hidden">
        <button
          onClick={() => toggleSection("simulations")}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">Simulation Activity ({data.assignedSimulations.length})</span>
          {expandedSections.simulations ? <ChevronUpIcon size={18} /> : <ChevronDownIcon size={18} />}
        </button>
        {expandedSections.simulations && data.assignedSimulations.length > 0 && (
          <div className="border-t border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2 font-medium text-gray-600">Simulation</th>
                  <th className="text-left p-2 font-medium text-gray-600">Assigned</th>
                  <th className="text-left p-2 font-medium text-gray-600">Completed</th>
                  <th className="text-left p-2 font-medium text-gray-600">In Progress</th>
                </tr>
              </thead>
              <tbody>
                {data.assignedSimulations.map((sim) => (
                  <tr key={sim.testId} className="border-t border-gray-50 hover:bg-gray-50">
                    <td className="p-2">
                      <p className="font-medium text-gray-900">{sim.testTitle || sim.testShortName || "Unknown"}</p>
                      {sim.testShortName && sim.testTitle && (
                        <p className="text-xs text-gray-500">{sim.testShortName}</p>
                      )}
                    </td>
                    <td className="p-2 text-gray-600">{sim.totalAssigned}</td>
                    <td className="p-2">
                      <span className="text-green-600 font-medium">{sim.completed}</span>
                    </td>
                    <td className="p-2">
                      <span className="text-blue-600 font-medium">{sim.inProgress}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {expandedSections.simulations && data.assignedSimulations.length === 0 && (
          <p className="p-3 text-gray-500 text-sm border-t border-gray-100">No simulations assigned</p>
        )}
      </div>

      {/* Available Simulations Section */}
      <div className="bg-white rounded-lg mb-3 overflow-hidden">
        <button
          onClick={() => toggleSection("measures")}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">Available Simulations ({data.availableSimulations.length})</span>
          {expandedSections.measures ? <ChevronUpIcon size={18} /> : <ChevronDownIcon size={18} />}
        </button>
        {expandedSections.measures && data.availableSimulations.length > 0 && (
          <div className="border-t border-gray-100 p-3">
            <div className="flex flex-wrap gap-2">
              {data.availableSimulations.map((sim) => (
                <span
                  key={sim.testId}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  {sim.testTitle || sim.testShortName || `Test #${sim.testId}`}
                </span>
              ))}
            </div>
          </div>
        )}
        {expandedSections.measures && data.availableSimulations.length === 0 && (
          <p className="p-3 text-gray-500 text-sm border-t border-gray-100">No available simulations</p>
        )}
      </div>

      {/* Organization Management Section */}
      <div className="bg-white rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection("management")}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">Organization Management</span>
          {expandedSections.management ? <ChevronUpIcon size={18} /> : <ChevronDownIcon size={18} />}
        </button>
        {expandedSections.management && (
          <div className="border-t border-gray-100 p-4 space-y-4">
            {/* Tags Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <TagIcon size={14} />
                  Tags
                </label>
                {!editingTags ? (
                  <button
                    onClick={() => setEditingTags(true)}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                  >
                    <EditIcon size={14} />
                    Edit
                  </button>
                ) : (
                  <button
                    onClick={handleSaveTags}
                    disabled={isUpdatingMetadata}
                    className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1 disabled:opacity-50"
                  >
                    {isUpdatingMetadata ? (
                      <div className="w-3 h-3 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <SaveIcon size={14} />
                    )}
                    Save
                  </button>
                )}
              </div>
              {editingTags ? (
                <input
                  type="text"
                  value={tagsValue}
                  onChange={(e) => setTagsValue(e.target.value)}
                  placeholder="Enter tags separated by commas..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-600">{tagsValue || "No tags"}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">Separate multiple tags with commas</p>
            </div>

            {/* Notes Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Notes</label>
                {!editingNotes ? (
                  <button
                    onClick={() => setEditingNotes(true)}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                  >
                    <EditIcon size={14} />
                    Edit
                  </button>
                ) : (
                  <button
                    onClick={handleSaveNotes}
                    disabled={isUpdatingMetadata}
                    className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1 disabled:opacity-50"
                  >
                    {isUpdatingMetadata ? (
                      <div className="w-3 h-3 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <SaveIcon size={14} />
                    )}
                    Save
                  </button>
                )}
              </div>
              {editingNotes ? (
                <textarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  placeholder="Enter notes about this organization..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                />
              ) : (
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{notesValue || "No notes"}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="border-t pt-4 flex flex-wrap gap-3">
              {/* Archive/Unarchive Button */}
              {!showArchiveConfirm ? (
                <button
                  onClick={() => setShowArchiveConfirm(true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                    isArchived
                      ? "bg-green-100 text-green-700 hover:bg-green-200"
                      : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                  }`}
                >
                  <ArchiveIcon size={16} />
                  {isArchived ? "Unarchive" : "Archive"}
                </button>
              ) : (
                <div className="flex items-center gap-2 bg-yellow-50 px-3 py-2 rounded-lg border border-yellow-200">
                  <span className="text-sm text-yellow-800">
                    {isArchived ? "Unarchive this organization?" : "Archive this organization?"}
                  </span>
                  <button
                    onClick={async () => {
                      await onArchive(data.organization.id, !isArchived)
                      setShowArchiveConfirm(false)
                    }}
                    disabled={isArchiving}
                    className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700 disabled:opacity-50"
                  >
                    {isArchiving ? "..." : "Yes"}
                  </button>
                  <button
                    onClick={() => setShowArchiveConfirm(false)}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                  >
                    No
                  </button>
                </div>
              )}

              {/* Delete Button */}
              <button
                onClick={() => onOpenDeleteModal(data.organization.id, data.organization.organizationName)}
                className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium"
              >
                <TrashIcon size={16} />
                Delete Organization
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const DataExplorerPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("")
  const [searchType, setSearchType] = useState<SearchType>("all")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null)
  const [expandedOrgId, setExpandedOrgId] = useState<number | null>(null)
  const [userDetails, setUserDetails] = useState<DataExplorerUserDetailsData | null>(null)
  const [orgDetails, setOrgDetails] = useState<DataExplorerOrgDetailsData | null>(null)
  const [loadingUserId, setLoadingUserId] = useState<number | null>(null)
  const [loadingOrgId, setLoadingOrgId] = useState<number | null>(null)
  // Organization management state
  const [orgMetadata, setOrgMetadata] = useState<{ tags: string; notes: string; isArchived: boolean }>({ tags: "", notes: "", isArchived: false })
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteOrgInfo, setDeleteOrgInfo] = useState<{ id: number; name: string } | null>(null)

  const [fetchUserDetails] = useLazyGetDataExplorerUserDetailsQuery()
  const [fetchOrgDetails] = useLazyGetDataExplorerOrgDetailsQuery()
  const [assignSimulation, { isLoading: isAssigningToUser }] = useAssignSimulationToUserMutation()
  const [unlockMeasure, { isLoading: isUnlocking }] = useUnlockMeasureForOrgMutation()
  const [assignSimToOrg, { isLoading: isAssigningToOrg }] = useAssignSimulationToOrgMutation()
  const [updateMetadata, { isLoading: isUpdatingMetadata }] = useUpdateOrgMetadataMutation()
  const [archiveOrg, { isLoading: isArchiving }] = useArchiveOrganizationMutation()
  const { data: availableMeasuresData } = useGetAvailableMeasuresQuery()
  const { data: availableSimsData } = useGetAvailableSimulationsQuery()

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const { data, isLoading, isFetching } = useGetAdminDataExplorerQuery(
    { q: debouncedQuery, type: searchType },
    { skip: debouncedQuery.length < 2 }
  )

  const results = data?.data || { users: [], simulations: [], organizations: [] }
  const hasResults =
    results.users.length > 0 ||
    results.simulations.length > 0 ||
    results.organizations.length > 0

  const searchTypes: { value: SearchType; label: string; icon: React.ReactNode }[] = [
    { value: "all", label: "All", icon: <ExploreIcon size={16} /> },
    { value: "users", label: "Users", icon: <UserIcon size={16} /> },
    { value: "simulations", label: "Simulations", icon: <AssessmentIcon size={16} /> },
    { value: "organizations", label: "Organizations", icon: <BuildingIcon size={16} /> },
  ]

  const handleUserClick = async (userId: number) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null)
      setUserDetails(null)
      return
    }
    setLoadingUserId(userId)
    setExpandedOrgId(null)
    setOrgDetails(null)
    try {
      const result = await fetchUserDetails(userId).unwrap()
      setUserDetails(result.data)
      setExpandedUserId(userId)
    } catch (error) {
      console.error("Failed to fetch user details:", error)
    } finally {
      setLoadingUserId(null)
    }
  }

  const handleOrgClick = async (orgId: number) => {
    if (expandedOrgId === orgId) {
      setExpandedOrgId(null)
      setOrgDetails(null)
      return
    }
    setLoadingOrgId(orgId)
    setExpandedUserId(null)
    setUserDetails(null)
    try {
      const result = await fetchOrgDetails(orgId).unwrap()
      setOrgDetails(result.data)
      setExpandedOrgId(orgId)
    } catch (error) {
      console.error("Failed to fetch org details:", error)
    } finally {
      setLoadingOrgId(null)
    }
  }

  const handleAssignSimulation = async (userId: number, testId: number) => {
    try {
      await assignSimulation({ userId, testId }).unwrap()
      // Refresh user details
      const result = await fetchUserDetails(userId).unwrap()
      setUserDetails(result.data)
    } catch (error) {
      console.error("Failed to assign simulation:", error)
    }
  }

  const handleUnlockMeasure = async (organizationId: number, measureId: number) => {
    try {
      await unlockMeasure({ organizationId, measureId }).unwrap()
      // Refresh org details
      const result = await fetchOrgDetails(organizationId).unwrap()
      setOrgDetails(result.data)
    } catch (error) {
      console.error("Failed to unlock measure:", error)
    }
  }

  const handleAssignSimToOrg = async (organizationId: number, testId: number) => {
    try {
      await assignSimToOrg({ organizationId, testId }).unwrap()
      // Refresh org details
      const result = await fetchOrgDetails(organizationId).unwrap()
      setOrgDetails(result.data)
    } catch (error) {
      console.error("Failed to assign simulation to org:", error)
    }
  }

  const handleUpdateOrgMetadata = async (organizationId: number, adminTags: string, adminNotes: string) => {
    try {
      await updateMetadata({ organizationId, adminTags, adminNotes }).unwrap()
      setOrgMetadata((prev) => ({ ...prev, tags: adminTags, notes: adminNotes }))
    } catch (error) {
      console.error("Failed to update org metadata:", error)
    }
  }

  const handleArchiveOrg = async (organizationId: number, archive: boolean) => {
    try {
      await archiveOrg({ organizationId, archive }).unwrap()
      setOrgMetadata((prev) => ({ ...prev, isArchived: archive }))
    } catch (error) {
      console.error("Failed to archive org:", error)
    }
  }

  const handleOpenDeleteModal = (organizationId: number, organizationName: string) => {
    setDeleteOrgInfo({ id: organizationId, name: organizationName })
    setDeleteModalOpen(true)
  }

  const handleOrgDeleted = () => {
    // Clear the org panel and refetch search results
    setExpandedOrgId(null)
    setOrgDetails(null)
    setDeleteModalOpen(false)
    setDeleteOrgInfo(null)
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <ExploreIcon size={24} className="text-orange-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Data Explorer
                  </h1>
                  <p className="text-gray-500">
                    Search across users, simulations, and organizations - click to view details
                  </p>
                </div>
              </div>
            </div>

            {/* Search Box */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <SearchIcon
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search by name, email, or keyword (min 2 characters)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  {isFetching && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Search Type Tabs */}
              <div className="flex gap-2 mt-4">
                {searchTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSearchType(type.value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      searchType === type.value
                        ? "bg-orange-100 text-orange-700 font-medium"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {type.icon}
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Results */}
            {debouncedQuery.length < 2 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <ExploreIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Start Searching
                </h3>
                <p className="text-gray-500">
                  Enter at least 2 characters to search across the platform data.
                </p>
              </div>
            ) : isLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
                  >
                    <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="space-y-3">
                      <div className="h-12 bg-gray-200 rounded"></div>
                      <div className="h-12 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : !hasResults ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <SearchIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Results Found
                </h3>
                <p className="text-gray-500">
                  No matching data found for {`"${debouncedQuery}"`}. Try a different search term.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Users Results */}
                {(searchType === "all" || searchType === "users") && results.users.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <UserIcon size={18} className="text-blue-600" />
                        <h2 className="font-semibold text-gray-900">
                          Users ({results.users.length})
                        </h2>
                        <span className="text-sm text-gray-500 ml-2">Click to view details</span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {results.users.map((user) => (
                        <div key={user.id}>
                          <div
                            onClick={() => handleUserClick(user.id)}
                            className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                              expandedUserId === user.id ? "bg-blue-50" : ""
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  expandedUserId === user.id ? "bg-blue-200" : "bg-blue-100"
                                }`}>
                                  {loadingUserId === user.id ? (
                                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <UserIcon size={20} className="text-blue-600" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {user.firstName || user.lastName
                                      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                                      : "No Name"}
                                  </p>
                                  <p className="text-sm text-gray-500">{user.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {user.organizationName && (
                                  <span className="flex items-center gap-1 text-sm text-gray-500 px-3 py-1 bg-gray-100 rounded-full">
                                    <BuildingIcon size={14} />
                                    {user.organizationName}
                                  </span>
                                )}
                                {expandedUserId === user.id ? (
                                  <ChevronUpIcon size={20} className="text-gray-400" />
                                ) : (
                                  <ChevronDownIcon size={20} className="text-gray-400" />
                                )}
                              </div>
                            </div>
                          </div>
                          {expandedUserId === user.id && userDetails && (
                            <UserDetailsPanel
                              data={userDetails}
                              onClose={() => {
                                setExpandedUserId(null)
                                setUserDetails(null)
                              }}
                              onAssignSimulation={handleAssignSimulation}
                              isAssigning={isAssigningToUser}
                              availableSimulations={availableSimsData?.data?.simulations || []}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Simulations Results */}
                {(searchType === "all" || searchType === "simulations") &&
                  results.simulations.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="p-4 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center gap-2">
                          <AssessmentIcon size={18} className="text-indigo-600" />
                          <h2 className="font-semibold text-gray-900">
                            Simulations ({results.simulations.length})
                          </h2>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {results.simulations.map((sim) => (
                          <div
                            key={sim.id}
                            className="p-4 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                <AssessmentIcon size={20} className="text-indigo-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{sim.title}</p>
                                {sim.shortName && (
                                  <p className="text-sm text-gray-500">{sim.shortName}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Organizations Results */}
                {(searchType === "all" || searchType === "organizations") &&
                  results.organizations.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="p-4 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center gap-2">
                          <BuildingIcon size={18} className="text-green-600" />
                          <h2 className="font-semibold text-gray-900">
                            Organizations ({results.organizations.length})
                          </h2>
                          <span className="text-sm text-gray-500 ml-2">Click to view details</span>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {results.organizations.map((org) => (
                          <div key={org.id}>
                            <div
                              onClick={() => handleOrgClick(org.id)}
                              className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                                expandedOrgId === org.id ? "bg-green-50" : ""
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    expandedOrgId === org.id ? "bg-green-200" : "bg-green-100"
                                  }`}>
                                    {loadingOrgId === org.id ? (
                                      <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                      <BuildingIcon size={20} className="text-green-600" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {org.organizationName}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="flex items-center gap-1 text-sm text-gray-600">
                                    <UsersIcon size={14} />
                                    {org.userCount} users
                                  </span>
                                  {expandedOrgId === org.id ? (
                                    <ChevronUpIcon size={20} className="text-gray-400" />
                                  ) : (
                                    <ChevronDownIcon size={20} className="text-gray-400" />
                                  )}
                                </div>
                              </div>
                            </div>
                            {expandedOrgId === org.id && orgDetails && (
                              <OrgDetailsPanel
                                data={orgDetails}
                                onClose={() => {
                                  setExpandedOrgId(null)
                                  setOrgDetails(null)
                                }}
                                onUnlockMeasure={handleUnlockMeasure}
                                onAssignSimulation={handleAssignSimToOrg}
                                onUpdateMetadata={handleUpdateOrgMetadata}
                                onArchive={handleArchiveOrg}
                                onOpenDeleteModal={handleOpenDeleteModal}
                                availableMeasures={availableMeasuresData?.data?.measures || []}
                                availableSimulations={availableSimsData?.data?.simulations || []}
                                isUnlocking={isUnlocking}
                                isAssigning={isAssigningToOrg}
                                isUpdatingMetadata={isUpdatingMetadata}
                                isArchiving={isArchiving}
                                initialTags={orgMetadata.tags}
                                initialNotes={orgMetadata.notes}
                                isArchived={orgMetadata.isArchived}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Organization Deletion Modal */}
      {deleteOrgInfo && (
        <OrgDeletionModal
          isOpen={deleteModalOpen}
          organizationId={deleteOrgInfo.id}
          organizationName={deleteOrgInfo.name}
          onClose={() => {
            setDeleteModalOpen(false)
            setDeleteOrgInfo(null)
          }}
          onDeleted={handleOrgDeleted}
        />
      )}
    </div>
  )
}

export default DataExplorerPage
